import { useEffect, useState } from "react";
import { Button, Flex, Separator, Text, TextArea } from "@radix-ui/themes";
import { LocationAutocomplete } from "@/components/ui/LocationAutocomplete";
import type { UpdateCreatorLocationDto } from "@/lib/lutely-api/gen/dtos";

export type InlineAboutEditSectionProps = {
  initialLocationText: string;
  initialLocationData: UpdateCreatorLocationDto | null | undefined;
  initialBio: string;
  isSaving: boolean;
  onSave: (
    bio: string,
    location: UpdateCreatorLocationDto | null,
  ) => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
  /** Rings the bio while it is still empty, for the Complete profile entry. */
  highlightRequired?: boolean;
  /** Reports the live draft so a parent can save every section at once. */
  onValuesChange?: (values: {
    bio: string;
    location: UpdateCreatorLocationDto | null;
  }) => void;
};

export function InlineAboutEditSection({
  initialLocationText,
  initialLocationData,
  initialBio,
  isSaving,
  onSave,
  onDirtyChange,
  highlightRequired = false,
  onValuesChange,
}: InlineAboutEditSectionProps) {
  const [locationText, setLocationText] = useState(initialLocationText);
  const [locationData, setLocationData] =
    useState<UpdateCreatorLocationDto | null>(initialLocationData ?? null);
  const [bio, setBio] = useState(initialBio);
  const [baseline, setBaseline] = useState({
    locationText: initialLocationText,
    locationData: initialLocationData ?? null,
    bio: initialBio,
  });

  const isDirty =
    bio !== baseline.bio ||
    locationText !== baseline.locationText ||
    JSON.stringify(locationData) !== JSON.stringify(baseline.locationData);

  /** A picked suggestion keeps its city/country split; free text becomes a city. */
  function buildLocation(): UpdateCreatorLocationDto | null {
    if (locationData) {
      return {
        city: locationData.city ?? null,
        state: locationData.state ?? null,
        country: locationData.country ?? null,
        zip: locationData.zip ?? null,
      };
    }
    return locationText.trim()
      ? { city: locationText.trim(), state: null, country: null, zip: null }
      : null;
  }

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onValuesChange?.({ bio, location: buildLocation() });
  }, [bio, locationText, locationData]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    // Reset the baseline only when the save actually landed: a rejected
    // onSave means the values on the server are still the old ones, so the
    // section must stay dirty. The parent surfaced the error already.
    try {
      await onSave(bio, buildLocation());
    } catch {
      return;
    }
    setBaseline({ locationText, locationData, bio });
  }

  function handleCancel() {
    setLocationText(baseline.locationText);
    setLocationData(baseline.locationData);
    setBio(baseline.bio);
  }

  return (
    <Flex direction="column" gap="4">
      <Separator className="w-full" />

      <Flex direction="column" gap="1">
        <Text
          as="label"
          htmlFor="creator-location"
          size="2"
          weight="medium"
          className="text-blue-900"
        >
          Location
        </Text>
        <LocationAutocomplete
          id="creator-location"
          value={locationText}
          onChange={(val) => {
            setLocationText(val);
            setLocationData(null);
          }}
          onLocationSelect={(loc) => setLocationData(loc)}
          placeholder="Where are you based?"
        />
      </Flex>

      <Flex direction="column" gap="1">
        <Text
          as="label"
          htmlFor="creator-bio"
          size="2"
          weight="medium"
          className="text-blue-900"
        >
          Bio
        </Text>
        <TextArea
          id="creator-bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Tell the world a little bit about yourself. The shorter the better."
          rows={4}
          className={
            highlightRequired && !bio.trim()
              ? "ring-2 ring-amber-200 rounded-[6px]"
              : undefined
          }
        />
        {highlightRequired && !bio.trim() && (
          <Text size="1" className="text-amber-600">
            Required to complete your profile
          </Text>
        )}
      </Flex>

      <Flex align="center" justify="end" gap="3">
        <button
          type="button"
          onClick={handleCancel}
          disabled={!isDirty}
          className="text-sm text-[#525966] hover:text-[#050C2B] transition-colors disabled:opacity-40 disabled:pointer-events-none"
        >
          Cancel
        </button>
        <Button
          size="2"
          radius="full"
          onClick={handleSave}
          disabled={!isDirty || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </Flex>
    </Flex>
  );
}
